import {
  useEffect,
  useState,
  type MouseEvent,
  type SyntheticEvent,
} from 'react';
import { useRecoilValue } from 'recoil';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import { isEqual } from 'lodash';
import { addableLabelsSelector } from '../../../models/selectors/device_selectors';
import { useTranslation } from '../../../contexts/TranslationContext';
import { company_controller } from '../../../controllers/company_controller';
import { device_controller } from '../../../controllers/device_controller';
import { deviceInterface } from '../../../models/interfaces/device_interfaces';
import { MHMLabel } from '../MHM/MHMLabel';
import { MHMDropdownSelect } from '../MHM/MHMDropdownSelect';
import { MHMIconButton } from '../MHM/MHMIconButton';
import { Clear } from '@mui/icons-material';
import { MHMButton } from '../MHM/MHMButton';
import { MHMDialog } from '../MHM/MHMDialog';
import { LabelInput } from '../shared/LabelInput';
import { alphanumeric_comparator } from '../../../utils/helper_functions';

function LabelChip({ selected }: { selected: string[] }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {selected.map(value => (
        <MHMLabel key={value} label={value} labelProps={{ medium: true }} />
      ))}
    </Box>
  );
}

type DeviceLabelProps = {
  selectedDevice: deviceInterface;
  canEditSession: boolean;
};

export function DeviceLabels({
  selectedDevice,
  canEditSession,
}: DeviceLabelProps) {
  const { t } = useTranslation();
  const addableLabels = useRecoilValue(addableLabelsSelector);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState('');

  function stopPropagation(event: SyntheticEvent) {
    event.stopPropagation();
  }

  function handleChange({ target: { value } }: SelectChangeEvent<string[]>) {
    setSelectedLabels(typeof value === 'string' ? value.split(',') : value);
  }

  async function saveLabels() {
    if (
      isEqual(
        selectedLabels,
        selectedDevice.labels.filter((x: string) => addableLabels.includes(x)),
      )
    ) {
      return;
    }

    await company_controller.updateDeviceLabels(
      selectedDevice.device_id,
      selectedLabels,
    );

    await device_controller.get_device_list();
    device_controller.select_device(selectedDevice.device_id);
  }

  function openDeleteModal(event: MouseEvent, label: string) {
    event.stopPropagation();
    setLabelToDelete(label);
    setIsDeleteModalOpen(true);
  }

  function closeDeleteModal() {
    setLabelToDelete('');
    setIsDeleteModalOpen(false);
  }

  async function deleteLabel() {
    if (!labelToDelete) {
      return;
    }

    closeDeleteModal();
    await company_controller.delete_label(labelToDelete);

    await device_controller.get_device_list();
    if (!selectedDevice.labels.includes(labelToDelete)) {
      return;
    }
    device_controller.select_device(selectedDevice.device_id);
  }

  async function handleUpdateLabel(oldLabel: string, newLabel: string) {
    try {
      await company_controller.updateLabel(oldLabel, newLabel);
      return true;
    } catch {
      return false; // something went wrong, reset to defaultValue
    }
  }

  useEffect(() => {
    setSelectedLabels(
      selectedDevice.labels.filter(label => addableLabels.includes(label)),
    );
  }, [selectedDevice, addableLabels]);

  return (
    <>
      <MHMDropdownSelect
        label={t('Add Label')}
        labelId="add-label-input-label"
        value={selectedLabels.slice().sort(alphanumeric_comparator)}
        onChange={handleChange}
        renderValue={selected => <LabelChip selected={selected} />}
        onClose={saveLabels}
        fullWidth
        disabled={!canEditSession}
        sx={{ minHeight: 48 }}
      >
        <MenuItem
          disableRipple
          selected={false}
          onClick={stopPropagation}
          sx={{ py: 1, maxWidth: '100%' }}
        >
          <LabelInput
            flex={1}
            textFieldSx={{ flex: 1 }}
            onClick={stopPropagation}
            onSave={newLabel => company_controller.create_label(newLabel)}
          />
        </MenuItem>
        {addableLabels.length ? (
          addableLabels.map(label => (
            <MenuItem key={label} value={label} sx={{ gap: 1, flex: 1 }}>
              <Checkbox
                checked={selectedLabels.includes(label)}
                disableRipple
              />
              <LabelInput
                defaultValue={label}
                gap={1}
                textFieldLabel=""
                textFieldSx={{ flex: 1 }}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
                sx={{ flex: 1 }}
                onSave={newLabel => handleUpdateLabel(label, newLabel)}
              />
              <MHMIconButton
                onClick={e => openDeleteModal(e, label)}
                aria-label="delete"
              >
                <Clear />
              </MHMIconButton>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled value="">
            <em>{t('No Labels available')}</em>
          </MenuItem>
        )}
      </MHMDropdownSelect>

      <Dialog open={isDeleteModalOpen} onClose={closeDeleteModal}>
        <DialogTitle textAlign="center">{t('Confirm Deletion')}</DialogTitle>

        <MHMDialog.Content>
          {t('This label will be deleted from all devices')}
        </MHMDialog.Content>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <MHMButton onClick={closeDeleteModal} color="inherit">
            {t('Cancel')}
          </MHMButton>
          <MHMButton onClick={deleteLabel} color="error">
            {t('Delete')}
          </MHMButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
